import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Layout from '@/layout/index.vue';
import { deepClone } from '@/utils/function_utils';
import path from 'path';
import resourceManagement from './institution-resource';
import institutionInformation from './institution-information';
import mpsManagement from './mps-management';
import setting from './company-setting';
Vue.use(VueRouter);

/**
 * ConstantRoutes
 * 没有权限需求的基本页面路由，所有角色都可以访问
 */
export const constantRoutes: RouteConfig[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import(/* webpackChunkName: "login" */ '@/views/login/Login.vue'),
  },
  {
    path: '/reset',
    name: 'Reset',
    component: () => import(/* webpackChunkName: "login" */ '@/views/login/reset.vue'),
  },
  {
    path: '/404',
    name: 'error',
    component: () => import(/* webpackChunkName: "error" */ '@/views/error-page/error.vue'),
  },
  {
    path: '/redirect',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '/redirect/:path(.*)',
        component: () => import(/* webpackChunkName: "redirect" */ '@/views/redirect/index.vue'),
      },
    ],
  },
  {
    path: '/empty',
    name: 'empty',
    meta: { hidden: true },
    component: () => import(/* webpackChunkName: "redirect" */ '@/views/redirect/empty.vue'),
  },
];

// 异步路由
export const asyncRoutes: RouteConfig[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/index',
    meta: {
      icon: 'home',
      title: 'summary',
      alwaysShow: true,
      canClick: true, // 有嵌套路由时，是否可以为路由的一级侧栏
      roles: ['applications']
    },
    children: [
      {
        path: '/index',
        name: 'index',
        component: () => import(/* webpackChunkName: "index" */ '../views/index/Index.vue'),
        meta: {
          hidden: true,
          showIndexTabs: true,
          activeMenu: '/',
          affix: true,
          title: 'index',
          roles: ['applications']
        },
      },
      {
        path: 'index/business-overview',
        name: 'businessOverview',
        component: () => import(/* webpackChunkName: "index" */ '../views/index/BusinessOverview.vue'),
        meta: {
          noDot: true,
          showIndexTabs: true,
          title: 'businessOverview',
          activeMenu: '/index/business-overview',
          roles: ['applicationsOverview']
        }
      },
      {
        path: 'index/business-statistics',
        name: 'businessStatistics',
        component: () => import(/* webpackChunkName: "index" */ '../views/index/BusinessStatistics.vue'),
        meta: {
          noDot: true,
          showIndexTabs: true,
          title: 'businessStatistics',
          activeMenu: '/index/business-statistics',
          roles: ['applicationsCount']
        }
      },
      {
        path: 'index/financial-settlement',
        name: 'financialSettlement',
        component: () => import(/* webpackChunkName: "index" */ '../views/index/FinancialSettlement.vue'),
        meta: {
          noDot: true,
          showIndexTabs: true,
          title: 'financialSettlement',
          activeMenu: '/index/financial-settlement',
          // roles: ['applications'] // 暂时测试用
          roles: ['applicationsSettlement']
        }
      },
      {
        path: 'index/student-query',
        name: 'studentQuery',
        component: () => import(/* webpackChunkName: "index" */ '../views/index/StudentQuery.vue'),
        meta: {
          hidden: true,
          activeMenu: '/',
          title: 'studentQuery',
          showIndexTabs: true,
          roles: ['applications']
        },
      },
      {
        path: 'index/application-list',
        name: 'applicationList',
        component: () => import(/* webpackChunkName: "index" */ '../views/index/CourseApplication.vue'),
        meta: {
          hidden: true,
          activeMenu: '/',
          showIndexTabs: true,
          title: 'applicationList',
          roles: ['applicationsDetail','applications']
        },
      },
      {
        path: 'index/student-information/:id',
        name: 'studentInformation',
        component: () => import(/* webpackChunkName: "index" */ '../views/index/StudentInformation.vue'),
        meta: {
          hidden: true,
          activeMenu: '/',
          showIndexTabs: true,
          title: 'studentInformation',
          roles: ['applications']
        },
      },
      {
        path: 'student-register',
        name: 'studentRegister',
        meta: {
          hidden: true,
          title: 'studentRegister',
          activeMenu: '/',
          roles: ['applications'],
        },
        // tslint:disable:max-line-length
        component: () => import(/* webpackChunkName: "stuManagement" */ '@/views/index/StudentRegister.vue'),
      },
      {
        path: 'student-application-form/:stuid',
        name: 'studentApplicationForm',
        meta: {
          title: 'studentApplicationForm',
          hidden: true,
          activeMenu: '/',
          roles: ['applications'],
        },
        // tslint:disable:max-line-length
        component: () => import(/* webpackChunkName: "stuManagement" */ '@/views/index/StudentApplicationForm.vue'),
      }
    ]
  },
  ...resourceManagement,
  // ...institutionInformation,
  ...mpsManagement,
  ...setting,
  { path: '*', redirect: '/404' },
];
const createRouter = () => new VueRouter({
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      // 当且仅当 popstate 导航 (通过浏览器的 前进/后退 按钮触发) 时才可用
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
  mode: 'history',
  base: process.env.BASE_URL,
  routes: constantRoutes,
});

const router = createRouter();

export function resetRouter() {
  const newRouter = createRouter();
  (router as any).matcher = (newRouter as any).matcher;
}

// 二级以上的菜单降级成二级菜单
const formatRouter = (routes: any, basePath = '/', list: any[] = [], parent: any) => {
  routes.map((item: any) => {
    item.path = path.resolve(basePath, item.path)
    const meta = item.meta || {};
    if (!meta.parent && parent) {
      meta.parent = parent.path;
      item.meta = meta;
    }
    if (item.redirect && item.redirect !== 'noRedirect') {
      item.redirect = path.resolve(basePath, item.redirect)
    }
    if (item.children && item.children.length > 0) {
      const arr = formatRouter(item.children, item.path, list, item);
      delete item.children;
      list.concat(arr);
    }
    list.push(item);
  })
  return list
}

// 菜单降级
export const getFlatRoutes = (routes: RouteConfig[]) => {
  return routes.map((child: RouteConfig) => {
    if (child.children && child.children.length > 0) {
      child.children = formatRouter(child.children, child.path, [], child);
    }
    return child;
  })
}

// 降级后的路由
export const flatRoutes = getFlatRoutes(deepClone(asyncRoutes, ['component']));

export default router;
